/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Injectable, inject } from '@angular/core';
import {merge, Observable, of, Subject} from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import {HistoryMenuModel} from './history-menu.model';
import {HistoryMenuConfigModel} from './history-menu-config.model';

@Injectable({
  providedIn: 'root'
})
export class HistoryMenuService {
  private historyMenuConfigModel = inject<HistoryMenuConfigModel>(HistoryMenuConfigModel);

  private itemsMap: Map<string, HistoryMenuModel> = new Map();

  private newMenuItemsSubject: Subject<HistoryMenuModel> = new Subject();
  private newItemsObservable!: Observable<Map<string, HistoryMenuModel>>;

  private clearMenuItemsSubject: Subject<boolean> = new Subject();
  private clearMenuItemsObservable!: Observable<Map<string, HistoryMenuModel>>;

  constructor() {
    const historyMenuConfigModel = this.historyMenuConfigModel;

    this.clearMenuItemsObservable = this.clearMenuItemsSubject.pipe(
      tap(() => {
        this.itemsMap.clear();
      }),
      switchMap(() => of(this.itemsMap))
    );

    this.newItemsObservable = this.newMenuItemsSubject.pipe(
      tap(data => {
        data.timestamp = new Date();

        if (data.key && this.itemsMap.get(data.key)) {
          this.itemsMap.delete(data.key);
        } else if (this.itemsMap.size >= (historyMenuConfigModel.maxBufferSize ?? 0)) {
          this.itemsMap.delete(this.itemsMap.keys().next()?.value);
        }

        if(data.key) {
          this.itemsMap.set(data.key, data);
        }
      }),
      switchMap(() => of(this.itemsMap))
    );
  }

  getAll(): Observable<HistoryMenuModel[]> {
    return merge(this.clearMenuItemsObservable, this.newItemsObservable).pipe(
      map((data): HistoryMenuModel[] => Array.from(data.values()))
    );
  }

  addItem(item: HistoryMenuModel): void {
    this.newMenuItemsSubject.next(item);
  }

  clear(): void {
    this.clearMenuItemsSubject.next(true);
  }

  registerToMenuHistory = (url: any, labelCb: any, type: any, key: any) => (source: Observable<any>) => {
    return source.pipe(
      tap(res => {
        this.addItem(
          new HistoryMenuModel(url, labelCb(res), type, key)
        );
      }));
  }

}
